import React, { useState } from "react";
import { Col, Dropdown } from "react-bootstrap";
import { ChevronDown, Map, MapPin, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDate, handleStopPropagation } from "components/common/Utils";
import { useDispatch } from "react-redux";
import {
  cloneSpace,
  deleteWorkspace,
  updateWorkspaceStatus,
} from "store/actions/workspaceSlice";
import { showAlert } from "store/reducers/alertReducer";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import LockIcon from "assets/images/icons/lock.svg";
import ClosureDetailsTooltip from "./ClosureDetailsTooltip";
import Riyal from "assets/images/icons/riyal.svg";

const WorkspaceCard = ({ item, className = "", isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Extract prices from the timeperiod array
  const prices = item?.timePeriods?.map((period) => period.price) || [];

  // Calculate the lowest and highest price
  const lowestPrice = Math.min(...prices);
  const highestPrice = Math.max(...prices);

  const handleDraft = (id, status) => {
    const updatedStatus = status === 1 ? 3 : 1;
    const payload = {
      id,
      status: updatedStatus, // 3 FOR DRAFT, 1 FOR PUBLISH
    };
    dispatch(updateWorkspaceStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteWorkspace(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleDuplicate = (id) => {
    const payload = {
      id,
    };
    dispatch(cloneSpace(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <>
      <Col lg={4} xxl={3}>
        <div
          className={`top_card_inner_section c_p ${className}`}
          onClick={() =>
            navigate(`/bookings/workspace/workspace-details?id=${item?._id}`)
          }
        >
          <div className="image_section position-relative">
            <img src={item?.imageUrls[0]} alt="" width={374} height={169} />
            <p className="price_tag">
              {lowestPrice === highestPrice ? (
                <>
                  <img
                    src={Riyal}
                    width={15}
                    height={15}
                    alt="riyal"
                    className="me-1 w_13"
                  />
                  {lowestPrice}
                </>
              ) : (
                <>
                  <img
                    src={Riyal}
                    width={15}
                    height={15}
                    alt="riyal"
                    className="me-1 w_13"
                  />
                  {lowestPrice} -{" "}
                  <img
                    src={Riyal}
                    width={15}
                    height={15}
                    alt="riyal"
                    className="me-1 w_13"
                  />
                  {highestPrice}
                </>
              )}
            </p>
          </div>
          <div className="content">
            <div
              className="g_between align-items-start gap-3"
              onClick={handleStopPropagation}
            >
              <h2>{item?.name || "-"}</h2>

              {item?.closureDetails && (
                <div
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  style={{ position: "relative", display: "inline-block" }}
                >
                  <img src={LockIcon} alt="lock" width={18} height={19} />

                  {showTooltip && (
                    <ClosureDetailsTooltip
                      item={item}
                      setShowTooltip={setShowTooltip}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <h3>
                <MapPin className="text_gray me-1" width={14} height={14} />
                {item?.cityId?.name || "-"}
              </h3>
              <h3>
                <Map className="text_gray me-1" width={14} height={14} />
                {item?.branchId?.name || "-"}
              </h3>
              <h3>
                <UserRound
                  className="text_gray me-1 mb-1"
                  width={14}
                  height={14}
                />
                {item?.totalSeats} {t("Seats")}
              </h3>
              <h3>
                <UserRound
                  className="text_gray me-1 mb-1"
                  width={14}
                  height={14}
                />
                {item?.seatsRemaining} {t("Remaining Seats")}
              </h3>
            </div>
            <div className="g_between bottom_div">
              <p>
                <span className="">{t("Last Edit on")}</span>:{" "}
                {formatDate(item?.updatedAt)} by{" "}
                <span>{item?.lastEditedBy?.name || "-"}</span>
              </p>
              <Dropdown
                className="custom_dropdown list_action_dropdown"
                drop={"down"}
                onClick={handleStopPropagation}
              >
                <Dropdown.Toggle>
                  <button className="list_edit_btn">
                    {t("Edit")} <ChevronDown width={18} height={18} />
                  </button>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  <Dropdown.Item
                    href="#"
                    onClick={() => handleDuplicate(item?._id)}
                  >
                    {t("Duplicate")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={() => handleDraft(item?._id, item.status)}
                  >
                    {item.status === 1 ? t("Draft") : t("Publish")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={() => {
                      navigate(
                        `/bookings/workspace/workspace-details?id=${item?._id}`
                      );
                    }}
                  >
                    {t("View")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    onClick={() => {
                      navigate(
                        `/bookings/workspace/edit-workspace?id=${item._id}`
                      );
                    }}
                  >
                    {t("Edit")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    className="text_danger"
                    onClick={() => {
                      setSelectedId(item._id);
                      setShowModal(true);
                    }}
                  >
                    {t("Delete")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
      </Col>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this item?")}
        isLoading={isLoading}
      />
    </>
  );
};

export default WorkspaceCard;
